import type { DefaultNamespace, ParseKeys } from '@if/core/i18n'
import { EMonthlyPaymentAgentOverrideMargin, EPaymentPlan, EProvince } from '@if/core/types'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { useMemo } from 'react'

// One knob for every free-typed worksheet input that triggers a priced request (term/plan
// options, funding recompute, payment rows) so they settle together.
export const WORKSHEET_INPUT_DEBOUNCE_MS = 500

export const isPromoPlan = (paymentPlanId: string | null | undefined): boolean =>
  !!paymentPlanId && paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string)

type InvalidTermReasonKey = Extract<
  ParseKeys<DefaultNamespace>,
  | 'worksheetCommon.errors.pmtTooHigh'
  | 'worksheetCommon.errors.aprTooHighTerm'
  | 'worksheetCommon.errors.storedTermAndPlanUnavailable'
>

export const getInvalidTermReasonKey = (reason: string | undefined): InvalidTermReasonKey | undefined => {
  switch (reason) {
    case 'pmtTooHigh':
      return 'worksheetCommon.errors.pmtTooHigh'
    case 'aprTooHigh':
      return 'worksheetCommon.errors.aprTooHighTerm'
    case 'storedUnavailable':
      return 'worksheetCommon.errors.storedTermAndPlanUnavailable'
    default:
      return undefined
  }
}

export const getMaxPmtAmountWithAgentLeeway = (
  paymentPlanId: string | undefined,
  maxPmtAmount: number,
): number => {
  if (paymentPlanId === (EPaymentPlan.RegularDailyInterests as string)) {
    return Math.round(maxPmtAmount * EMonthlyPaymentAgentOverrideMargin.RegularDailyInterests * 100) / 100
  }
  if (paymentPlanId) {
    return maxPmtAmount + EMonthlyPaymentAgentOverrideMargin.PromoCode
  }
  return maxPmtAmount
}

export interface AprCheckResult {
  tooHigh: boolean
  effectiveRate: number
  threshold: number
}

// Backend parity: Newfoundland uses the province high-cost rate; too-high only when strictly above threshold.
export const useAprCheck = (
  computedInfo: FundingComputedDto | null,
  stateIso: EProvince | undefined,
  effectiveRateThreshold: number,
  provinceConfigs: ProvinceConfigs,
): AprCheckResult =>
  useMemo(() => {
    const effectiveRate = computedInfo?.effectiveRate ?? 0

    const nlRate = stateIso === EProvince.Newfoundland ? (provinceConfigs[stateIso]?.highCostRate ?? 0) : 0
    const threshold = nlRate > 0 ? nlRate : effectiveRateThreshold
    const tooHigh = !!computedInfo && threshold > 0 && effectiveRate > threshold
    return { tooHigh, effectiveRate, threshold }
  }, [computedInfo, stateIso, effectiveRateThreshold, provinceConfigs])


/** @deprecated Use useAprCheck instead for the full APR check result. */
export const useAprTooHigh = (
  computedInfo: FundingComputedDto,
  stateIso: EProvince | undefined,
  provinceConfigs: ProvinceConfigs,
  financingConfig?: FinancingConfigDto,
): boolean => {
  const { tooHigh } = useAprCheck(
    computedInfo,
    stateIso,
    financingConfig?.effectiveRateThreshold ?? 0,
    provinceConfigs,
  )
  return tooHigh
}
